<template>
    <div
        v-if="hasSocialProof"
        class="social-proof-strip d-inline-flex flex-wrap align-items-center py-2 px-3 rounded-pill"
        :class="[
            compact ? 'social-proof-compact' : '',
            align === 'left' ? 'justify-content-start' : 'justify-content-center'
        ]"
        data-test="social-proof-strip"
    >
        <!-- High Claims (25+): Overlapping Avatar Cluster -->
        <div v-if="hasHighClaimCount" class="social-proof-avatars d-flex align-items-center mr-2" aria-hidden="true">
            <div
                v-for="(avatar, idx) in visibleAvatars"
                :key="idx"
                class="social-proof-avatar d-flex align-items-center justify-content-center font-weight-bold"
                :style="{
                    backgroundColor: `hsl(${avatar.hue}, 65%, 42%)`,
                    zIndex: visibleAvatars.length - idx
                }"
                :title="`Engineer (${avatar.initial})`"
            >
                {{ avatar.initial }}
            </div>
            <div
                v-if="additionalCount > 0"
                class="social-proof-avatar social-proof-more d-flex align-items-center justify-content-center font-weight-bold"
                :style="{ zIndex: 0 }"
            >
                +{{ additionalCount }}
            </div>
        </div>

        <!-- Social Proof Text & Metric -->
        <div class="social-proof-text text-light-muted small d-inline-flex flex-wrap align-items-center">
            <span v-if="hasHighClaimCount" class="proof-count-label">
                Joined by <strong class="text-white">{{ socialProof.claimCount }} {{ socialProof.claimCount === 1 ? 'engineer' : 'engineers' }}</strong> in active beta
            </span>
            <span v-else class="proof-early-trust d-inline-flex flex-wrap align-items-center">
                <span class="d-inline-flex align-items-center mr-2">
                    <i class="bi bi-shield-check text-success mr-1"></i>
                    <strong class="text-white">Free Pro in Beta</strong>
                </span>
                <span class="text-muted mr-2">&bull;</span>
                <span class="d-inline-flex align-items-center mr-2">
                    <i class="bi bi-tag-fill text-warning mr-1"></i>
                    <span>50% Early-Bird (Beta + Month 1)</span>
                </span>
                <span class="text-muted mr-2">&bull;</span>
                <span class="d-inline-flex align-items-center">
                    <i class="bi bi-hdd-network text-info mr-1"></i>
                    <span>EU Data Sovereignty</span>
                </span>
            </span>

            <!-- Rating badge if 3 or more verified ratings exist -->
            <span v-if="hasRating" class="proof-rating-section d-inline-flex align-items-center ml-2 pl-2 border-left-subtle">
                <span class="star-cluster text-warning mr-1" aria-label="Rating">
                    <i class="bi bi-star-fill"></i>
                </span>
                <strong class="text-white mr-1">{{ formattedRating }}</strong>
                <span class="text-light-muted mr-1">/5.0</span>
                <span class="rating-subtle-count">({{ socialProof.ratingCount }} reviews)</span>
            </span>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'SocialProofStrip',
        props: {
            socialProof: {
                type: Object,
                default: () => null
            },
            compact: {
                type: Boolean,
                default: false
            },
            align: {
                type: String,
                default: 'center'
            }
        },
        computed: {
            hasSocialProof() {
                return true;
            },
            hasHighClaimCount() {
                return !!(this.socialProof && this.socialProof.claimCount >= 25);
            },
            visibleAvatars() {
                if (!this.hasHighClaimCount || !this.socialProof || !Array.isArray(this.socialProof.avatars)) return [];
                return this.socialProof.avatars.slice(0, 5);
            },
            additionalCount() {
                if (!this.socialProof || !this.socialProof.claimCount) return 0;
                const shown = this.visibleAvatars.length;
                const remaining = this.socialProof.claimCount - shown;
                return remaining > 0 ? remaining : 0;
            },
            hasRating() {
                return !!(
                    this.socialProof &&
                    this.socialProof.averageRating &&
                    this.socialProof.ratingCount >= 3
                );
            },
            formattedRating() {
                if (!this.hasRating) return '';
                return Number(this.socialProof.averageRating).toFixed(1);
            }
        }
    };
</script>

<style scoped>
    .social-proof-strip {
        background: rgba(15, 23, 42, 0.65);
        border: 1px solid rgba(56, 189, 248, 0.2);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
        transition: all 0.25s ease-in-out;
        max-width: 100%;
    }

    .social-proof-strip:hover {
        border-color: rgba(56, 189, 248, 0.4);
        box-shadow: 0 6px 20px rgba(56, 189, 248, 0.15);
    }

    .social-proof-compact {
        padding: 0.25rem 0.75rem !important;
        background: rgba(15, 23, 42, 0.45);
    }

    .social-proof-avatars {
        display: inline-flex;
    }

    .social-proof-avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        color: #ffffff;
        font-size: 0.72rem;
        border: 2px solid #0f172a;
        margin-left: -8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        user-select: none;
    }

    .social-proof-avatar:first-child {
        margin-left: 0;
    }

    .social-proof-more {
        background-color: #334155 !important;
        color: #94a3b8;
        font-size: 0.65rem;
    }

    .social-proof-text {
        line-height: 1.3;
    }

    .border-left-subtle {
        border-left: 1px solid rgba(148, 163, 184, 0.25);
    }

    .star-cluster {
        font-size: 0.8rem;
    }

    .rating-subtle-count {
        color: #94a3b8;
        font-size: 0.75rem;
    }

    @media (max-width: 576px) {
        .social-proof-strip {
            border-radius: 16px !important;
            padding: 0.5rem 0.75rem !important;
        }

        .proof-rating-section {
            border-left: none;
            margin-left: 0 !important;
            padding-left: 0 !important;
            width: 100%;
            margin-top: 0.25rem;
        }
    }
</style>
